import { Badge, Heading, Icon } from '@nexa/ui';
import { KnowledgeFileRow } from './KnowledgeFileRow.tsx';

interface BadgeRow {
  readonly kind: 'badge';
  readonly code: string;
  readonly tint: 'danger' | 'success' | 'brand';
  readonly label: string;
  readonly trailing: string;
}

interface IconRow {
  readonly kind: 'icon';
  readonly label: string;
  readonly trailing: string;
}

export interface KnowledgeCardProps {
  readonly icon: 'library' | 'book-open';
  readonly iconTone: 'brand' | 'violet';
  readonly title: string;
  readonly beta: boolean;
  readonly betaLabel: string;
  readonly body: string;
  readonly rows: readonly (BadgeRow | IconRow)[];
}

const ICON_TONE = {
  brand: 'text-brand-600',
  violet: 'text-violet-fg',
} as const;

/**
 * Một card của `#knowledge` — `template.html:954-961` và `:962-972`.
 *
 * Badge BETA ở đây là biến thể CÓ PILL (`:963`), khác biến thể chữ trần của `#modules`
 * (`:802 :803`). `reference.md` §3 chỉ tả biến thể chữ trần và dẫn nhầm dòng `:804` — dòng đó
 * không có BETA nào. `Badge` đã có sẵn tone `beta-pill` dựng đúng cho chỗ này.
 *
 * `overflow-hidden` là của thiết kế: vùng list có nền riêng chạy sát mép, cần bo góc cắt theo vỏ.
 *
 * Đoạn văn dùng `--text-lead-sm` (15px, nhịp dòng 1.6) — bậc RIÊNG cho vai đoạn văn của cỡ 15,
 * thêm ở lô C sau khi ĐO. Trước đó bảng §4.1 ánh 15 vào `--text-title-sm` (15.5px), và nửa pixel
 * đó làm đoạn văn này đổi từ 2 lên 3 dòng ở bề rộng thật của khối (552px), kéo 6 hàng file dưới
 * nó lệch 19.8px. Ép về đúng 15px thì trở lại 2 dòng, ép nhịp dòng thì KHÔNG — nên thủ phạm là
 * cỡ chữ, không phải nhịp dòng. Cùng lớp lỗi với ca 42px của `#modules`.
 */
export function KnowledgeCard({
  icon,
  iconTone,
  title,
  beta,
  betaLabel,
  body,
  rows,
}: KnowledgeCardProps) {
  return (
    <div className="h-full overflow-hidden rounded-card-lg border border-ink-100">
      <div className="px-7 py-[26px]">
        <div className="flex items-center gap-2.5">
          <Icon name={icon} size={19} className={ICON_TONE[iconTone]} />
          <Heading level={3} size="title-lg">
            {title}
          </Heading>
          {beta && <Badge tone="beta-pill">{betaLabel}</Badge>}
        </div>
        <p className="mt-3 text-lead-sm text-ink-600 text-pretty">{body}</p>
      </div>

      <div className="flex flex-col gap-[9px] border-t border-ink-100 bg-surface-muted px-7 py-[18px]">
        {rows.map((row) => (
          <KnowledgeFileRow key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}
